import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { PaymentRequest, StripeError } from '@stripe/stripe-js'
import axios from 'axios'
import { getMetaCookies } from '../../../utils/metaCookies'
import {
  centsToValue,
  INITIAL_FEE_CENTS,
  initMetaPixel,
  MONTHLY_PRICE_CENTS,
  PIXEL_FLUSH_DELAY_MS
} from '../../../utils/metaPixel'
import React, { useEffect, useRef, useState } from 'react'
import ReactPixel from 'react-facebook-pixel'
import { TailSpin } from 'react-loading-icons'
import { useLocation } from 'react-router-dom'
import Modal from '../../common/Modal/Modal'
import EmailCheckCard from '../EmailCheckCard/EmailCheckCard'
import {
  ApplyButton,
  ApplePayWrapper,
  BreakdownContainer,
  BreakDownText,
  BreakDownWrapper,
  Container,
  FlexRow,
  FormInput,
  FormInputContainerLarge,
  FormInputContainerSmall,
  FormLabel,
  InfoContainer,
  Link,
  OrDivider,
  OrDividerLine,
  OrDividerText,
  PromoChevron,
  PromoFields,
  PromoToggle,
  ReferredBy,
  stripeStyle,
  SubmitButton,
  TOS
} from './PurchaseForm.styled'
import { FormState, PurchaseFormProps } from './PurchaseForm.types'
import { clearStoredReferral } from '../../../utils/referral'
import { useStoredReferral } from '../../../hooks/useStoredReferral'

function PurchaseForm({
  userInfo,
  product,
  password,
  couponDetails,
  setCouponDetails,
  total,
  trialDays,
  setState,
  state
}: PurchaseFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { search } = useLocation()

  // Affiliate referral stored from /r/:code or ?ref= (see utils/referral); not editable at checkout
  const referral = useStoredReferral()

  const [formState, setFormState] = useState<FormState>({
    fullName: '',
    email: '',
    coupon: '',
  })

  const [showPromo, setShowPromo] = useState(false)

  const [showEmailCheckModal, setShowEmailCheckModal] = useState(false)
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [canMakeApplePay, setCanMakeApplePay] = useState(false)

  const pendingPaymentMethodId = useRef<string | null>(null)
  const pendingPayerName = useRef<string>('')
  const pendingPayerEmail = useRef<string>('')
  const signupTracked = useRef(false)

  const toggleEmailCheckModal = () => setShowEmailCheckModal((prev) => !prev)

  const calcAmount = () => {
    if (userInfo.waiveInitial) return 999
    if (trialDays > 0 && !userInfo.trialed) return 0
    if (couponDetails.isValid) return Math.max(0, 4999 - couponDetails.amountOff) + 999
    return 5998
  }

  // What a trial signup pays when the trial ends, in cents: the recurring price from the Stripe
  // subscription, plus the initial fee (less coupon) that the API adds to that first invoice unless waived
  const trialEndAmountCents = (subscription: any) => {
    const recurring =
      subscription?.items?.data?.[0]?.price?.unit_amount ??
      subscription?.plan?.amount ??
      MONTHLY_PRICE_CENTS
    const amountOff =
      couponDetails.isValid && Number.isFinite(couponDetails.amountOff) ? couponDetails.amountOff : 0
    const initial = userInfo.waiveInitial ? 0 : Math.max(0, INITIAL_FEE_CENTS - amountOff)
    return recurring + initial
  }

  const trackSignup = (subscription: any) => {
    if (signupTracked.current) return
    signupTracked.current = true

    if (subscription.status === 'trialing') {
      // no money collected yet; the real Purchase is sent server-side when the trial converts
      const cents = trialEndAmountCents(subscription)
      ReactPixel.track('StartTrial', {
        ...(cents > 0 && { value: centsToValue(cents), predicted_ltv: centsToValue(cents) }),
        currency: 'USD',
        content_type: 'product',
        content_name: 'swiftsole_v3',
      })
      return
    }

    // amount_due is what Stripe charged for the first invoice, after coupons (amount_paid is still 0
    // on the 3D Secure path, where the invoice object predates confirmation)
    const charged = subscription.latest_invoice?.amount_due ?? calcAmount()
    if (charged > 0) {
      // The API sends this same Purchase server-side (Conversions API) with event_id = the invoice id,
      // so Meta dedupes the pair; track() can't pass eventID, hence fbq directly
      const eventId = subscription.latest_invoice?.id
      ReactPixel.fbq(
        'track',
        'Purchase',
        {
          value: centsToValue(charged),
          currency: 'USD',
          content_type: 'product',
          content_name: 'swiftsole_v3',
        },
        ...(eventId ? [{ eventID: eventId }] : [])
      )
    }
  }

  const onSubscriptionComplete = (result: any) => {
    if (!result) return null

    // Payment was successful.
    if (
      result.subscription.status === 'active' ||
      result.subscription.status === 'trialing'
    ) {
      trackSignup(result.subscription)
      clearStoredReferral()

      // keep the button in its submitting state until the page changes
      const destination = userInfo?.discord
        ? '/success'
        : `/complete-account-setup/${result.subscription.userId}`
      setTimeout(() => {
        window.location.href = destination
      }, PIXEL_FLUSH_DELAY_MS)
      return
    }

    setState((prev) => ({
      ...prev,
      isSubmitting: false,
    }))
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handlePaymentThatRequiresCustomerAction = (result: any) => {
    if (!result) return null

    const {
      subscription,
      paymentMethodId,
      isRetry,
    }: any = result

    if (
      subscription &&
      (subscription.status === 'active' || subscription.status === 'trialing')
    ) {
      // Subscription is active, no customer actions required.
      return { subscription, paymentMethodId }
    }

    if (subscription && subscription.code && subscription.message) {
      throw subscription.message
    }
    // If it's a first payment attempt, the payment intent is on the subscription latest invoice.
    // If it's a retry, the payment intent will be on the invoice itself.
    let paymentIntent = subscription.latest_invoice.payment_intent
    if (
      paymentIntent.status === 'requires_action' ||
      (isRetry === true && paymentIntent.status === 'requires_payment_method')
    ) {
      // Block native form submission.
      if (!stripe) {
        return
      }

      return stripe
        .confirmCardPayment(paymentIntent.client_secret, {
          payment_method: paymentMethodId,
        })
        .then((result) => {
          if (result.error) {
            throw result.error.message
          } else {
            if (result.paymentIntent.status === 'succeeded') {
              // Show a success message to your customer.
              subscription.status = 'active'
              return {
                subscription: subscription,
                paymentMethodId: paymentMethodId,
              }
            }
          }
        })
        .catch((error) => {
          setState((prev) => ({
            ...prev,
            error,
          }))
        })
    } else {
      // No customer action needed.
      return { subscription, paymentMethodId }
    }
  }

  const handleRequiresPaymentMethod = (result: any) => {
    if (!result) return null

    const {
      subscription,
      paymentMethodId,
    }: any = result

    if (
      subscription.status === 'active' ||
      subscription.status === 'trialing'
    ) {
      // subscription is active, no customer actions required.
      return { subscription, paymentMethodId }
    } else if (
      subscription.latest_invoice.payment_intent.status ===
      'requires_payment_method'
    ) {
      throw new Error('Your card was declined.')
    } else {
      return { subscription, paymentMethodId }
    }
  }

  const handleEmailCheckContinue = () => {
    toggleEmailCheckModal()
    if (pendingPaymentMethodId.current) {
      submitPayment(
        pendingPaymentMethodId.current,
        pendingPayerName.current,
        pendingPayerEmail.current,
        true
      )
    }
  }

  const submitPayment = async (
    paymentMethodId: string,
    name: string,
    email: string,
    proceedWithoutEmailCheck: boolean
  ): Promise<void> => {
    const { coupon } = formState

    try {
      fetch(`/api/v1/releases/${password}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullname: name,
          coupon,
          referralCode: referral ? referral.code : undefined,
          emailAddress: email,
          cardToken: paymentMethodId,
          proceedWithoutEmailCheck,
          ...getMetaCookies(),
        }),
        credentials: 'include',
      })
        .then(async (response) => {
          if (response.status === 402) {
            let data = await response.json()
            throw data
          }

          if (response.status === 418) {
            setState((prev) => ({
              ...prev,
              isSubmitting: false,
            }))
            toggleEmailCheckModal()
            return null
          }

          return response.json()
        })
        .then((result) => {
          if (!result) return null

          if (result.error || result.message) {
            throw result.error
              ? result.error.message
              : result.message
              ? result.message
              : result.error
          }
          return result.data
        })
        .then((result) => {
          if (!result) return null

          return {
            paymentMethodId,
            subscription: result,
          }
        })
        .then(handlePaymentThatRequiresCustomerAction)
        .then(handleRequiresPaymentMethod)
        .then(onSubscriptionComplete)
        .catch((error) => {
          setState((prev) => ({
            ...prev,
            isSubmitting: false,
            error: error.message
              ? error.message
              : error.error
              ? error.error.message
              : error
              ? error
              : 'Something went wrong. Please try again.',
          }))
          console.log(error)
        })
    } catch (err: any) {
      console.log('[error]', err.response)

      if (err.response && err.response.status === 402) {
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
          error: err.response.data.message,
        }))
      } else {
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
          error: `Something went wrong. Please try again.`,
        }))
      }
    }
  }

  const handleSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    proceedWithoutEmailCheck = false
  ) => {
    if (event) {
      event.preventDefault()
    }

    if (state.isSubmitting) return

    if (!stripe || !elements) {
      return
    }

    setState((prev) => ({
      ...prev,
      isSubmitting: true,
    }))

    const cardElement = elements.getElement(CardNumberElement)

    if (!cardElement) {
      setState((prev) => ({ ...prev, isSubmitting: false }))
      return
    }

    const { fullName, email } = formState

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: fullName,
      },
    })

    if (error) {
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        error: (error as StripeError).message as string,
      }))
      console.log('[error]', error)
      return
    }

    if (paymentMethod) {
      pendingPaymentMethodId.current = paymentMethod.id
      pendingPayerName.current = fullName
      pendingPayerEmail.current = userInfo.email || email
      await submitPayment(paymentMethod.id, fullName, userInfo.email || email, proceedWithoutEmailCheck)
    } else {
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
      }))
    }
  }

  const handleApplyCoupon = async (coupon: string) => {
    if (userInfo.waiveInitial) {
      return setState((prev) => ({
        ...prev,
        error: 'Coupon not valid for recurring fee',
      }))
    }

    setState((prev) => ({
      ...prev,
      error: '',
    }))

    console.log(coupon)

    if (coupon !== '') {
      try {
        const { data } = await axios.post(
          '/api/v1/releases/apply-coupon',
          {
            coupon,
          },
          { withCredentials: true }
        )

        if (data.success && data.response.valid) {
          setCouponDetails({
            isValid: true,
            amountOff: data.response.amount_off,
            name: data.response.name,
          })
        } else {
          setState((prev) => ({
            ...prev,
            error: 'Invalid coupon applied',
          }))
          setCouponDetails({ isValid: false, amountOff: 0, name: '' })
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: 'Invalid coupon applied',
        }))
        setCouponDetails({ isValid: false, amountOff: 0, name: '' })
      }
    } else {
      setCouponDetails({ isValid: false, amountOff: 0, name: '' })
    }
  }

  const getPrice = () => {
    if (product === 'swiftsole_v3') {
      return '$9.99/month'
    }
    return ''
  }

  const getInitialPrice = () => {
    if (userInfo.waiveInitial === true) {
      return ''
    } else if (product === 'swiftsole_v3') {
      return 'Initial $49.99 +'
    } else {
      return ''
    }
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  useEffect(() => {
    initMetaPixel(userInfo)
  }, [])

  useEffect(() => {
    if (search) {
      const params = new URLSearchParams(search)
      const code = params.get('code')
      if (code) {
        setFormState((prev) => ({
          ...prev,
          coupon: code,
        }))
        setShowPromo(true)
        handleApplyCoupon(code)
      }
    }
  }, [])

  // Initialize Apple Pay / PaymentRequest when Stripe loads
  useEffect(() => {
    if (!stripe) return
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'SwiftSole V3', amount: calcAmount() },
      requestPayerName: true,
      requestPayerEmail: !userInfo.email,
    })
    pr.canMakePayment().then((result) => {
      if (result?.applePay) {
        setPaymentRequest(pr)
        setCanMakeApplePay(true)
      }
    })
  }, [stripe])

  // Update Apple Pay amount when the coupon, or a referral offer (setup fee, trial), changes
  useEffect(() => {
    if (!paymentRequest) return
    paymentRequest.update({ total: { label: 'SwiftSole V3', amount: calcAmount() } })
  }, [couponDetails, paymentRequest, trialDays, userInfo.waiveInitial])

  // Register Apple Pay paymentmethod event listener
  useEffect(() => {
    if (!paymentRequest) return
    const handler = async (ev: any) => {
      ev.complete('success') // must be called promptly before async work
      const name = ev.payerName || formState.fullName
      const email = ev.payerEmail || userInfo.email || formState.email
      pendingPaymentMethodId.current = ev.paymentMethod.id
      pendingPayerName.current = name
      pendingPayerEmail.current = email
      setState((prev) => ({ ...prev, isSubmitting: true }))
      await submitPayment(ev.paymentMethod.id, name, email, false)
    }
    paymentRequest.on('paymentmethod', handler)
    return () => {
      paymentRequest.off('paymentmethod', handler)
    }
  }, [paymentRequest, formState.fullName, formState.email])

  return (
    <>
    <Container>
      <InfoContainer>
        {canMakeApplePay && paymentRequest && (
          <>
            <FormInputContainerLarge>
              <ApplePayWrapper>
                <PaymentRequestButtonElement
                  options={{
                    paymentRequest,
                    style: {
                      paymentRequestButton: {
                        type: 'buy',
                        theme: 'light',
                        height: '60px',
                      },
                    },
                  }}
                />
              </ApplePayWrapper>
            </FormInputContainerLarge>
            <FormInputContainerLarge>
              <OrDivider>
                <OrDividerLine />
                <OrDividerText>or pay with card</OrDividerText>
                <OrDividerLine />
              </OrDivider>
            </FormInputContainerLarge>
          </>
        )}

        <FormInputContainerLarge>
          <FormLabel>Full Name</FormLabel>
          <FormInput
            name="fullName"
            placeholder="John Doe"
            value={formState.fullName}
            onChange={handleChange}
          />
        </FormInputContainerLarge>

        {!userInfo.email && (
          <FormInputContainerLarge>
            <FormLabel>Email Address</FormLabel>
            <FormInput
              name="email"
              placeholder="john.doe@swiftsole.app"
              value={formState.email}
              onChange={handleChange}
            />
          </FormInputContainerLarge>
        )}

        <FormInputContainerLarge>
          <FormLabel>Card Number</FormLabel>
          <CardNumberElement
            className="stripe-input-neutral"
            options={stripeStyle}
          />
        </FormInputContainerLarge>

        <FormInputContainerSmall>
          <FormLabel>Expiration Date</FormLabel>
          <CardExpiryElement
            className="stripe-input-neutral"
            options={stripeStyle}
          />
        </FormInputContainerSmall>

        <FormInputContainerSmall>
          <FormLabel>Security Code</FormLabel>
          <CardCvcElement
            className="stripe-input-neutral"
            options={stripeStyle}
          />
        </FormInputContainerSmall>

        <FormInputContainerLarge>
          <PromoToggle type="button" onClick={() => setShowPromo((p) => !p)}>
            <span>Have a promo code?</span>
            <PromoChevron open={showPromo}>▼</PromoChevron>
          </PromoToggle>
        </FormInputContainerLarge>

        <PromoFields open={showPromo}>
          <FormInputContainerLarge>
            <FormLabel>Coupon code</FormLabel>
            <FlexRow>
              <FormInput
                name="coupon"
                placeholder="COUPON10"
                value={formState.coupon}
                onChange={handleChange}
              />
              <ApplyButton onClick={() => handleApplyCoupon(formState.coupon)}>
                Apply
              </ApplyButton>
            </FlexRow>
          </FormInputContainerLarge>
        </PromoFields>

        {referral && (
          <FormInputContainerLarge>
            <ReferredBy>
              Referred by {referral.displayName || referral.code}
              {referral.offer &&
                `. New customers get no setup fee${
                  referral.offer.trialDays > 0 ? ` and a ${referral.offer.trialDays}-day free trial` : ''
                }.`}
            </ReferredBy>
          </FormInputContainerLarge>
        )}
      </InfoContainer>

      <BreakdownContainer>
        {(trialDays > 0 && !userInfo.trialed) && (
          <BreakDownWrapper>
            <p>Trial</p>
            <BreakDownText>
              Subscription Starts After {trialDays} Days
            </BreakDownText>
          </BreakDownWrapper>
        )}
        {(trialDays > 0 && !userInfo.trialed) && (
          <BreakDownWrapper>
            <p>Standard Price</p>
            <BreakDownText>
              {getInitialPrice() + ' ' + getPrice()}
            </BreakDownText>
          </BreakDownWrapper>
        )}

        {couponDetails.isValid === true && (
          <BreakDownWrapper>
            <p>Promo Code {couponDetails.name && `(${couponDetails.name})`}</p>
            <p>
              -
              {couponDetails?.isValid &&
                formatter.format(couponDetails.amountOff / 100)}
            </p>
          </BreakDownWrapper>
        )}

        <BreakDownWrapper largeText>
          <p>{(trialDays > 0 && !userInfo.trialed) ? 'Total After Trial' : 'Total'}</p>
          <p>{total}</p>
        </BreakDownWrapper>
      </BreakdownContainer>

      <SubmitButton onClick={handleSubmit}>
        {state.isSubmitting ? (
          <TailSpin />
        ) : (trialDays > 0 && !userInfo.trialed) ? (
          `Subscribe To Free Trial`
        ) : (
          'Subscribe'
        )}
      </SubmitButton>

      <TOS>
        <span>
          {(trialDays > 0 && !userInfo.trialed) 
            ? 'You will become a monthly subscriber effective immediately, and you will be charged at the end of your trial. '
            : ''}
          By subscribing to SwiftSole you agree to our
        </span>{' '}
        <Link href="/tos" target="_blank">
          Terms of Service
        </Link>
      </TOS>
    </Container>

    <Modal
      showModal={showEmailCheckModal}
      toggleModal={toggleEmailCheckModal}
    >
      <EmailCheckCard toggleModal={toggleEmailCheckModal} handleContinue={handleEmailCheckContinue} />
    </Modal>

    </>
  )
}

export default PurchaseForm
